import { createPresence } from "@solid-primitives/presence";
import { type AnimationParams, animate, stagger } from "animejs";
import { type Accessor, type Component, type JSX, Show, createEffect, createSignal, on, onCleanup } from "solid-js";

export type StaggeredAnimationProps = {
  ref: (el: HTMLElement) => void;
  isMounted: Accessor<boolean>;
  isVisible: Accessor<boolean>;
  totalDuration: Accessor<number>;
};

export const AnimateStaggeredChildren: Component<{
  show: boolean;
  children: (animationProps: StaggeredAnimationProps) => JSX.Element;
  /**
   * Time in milliseconds to delay the animation for the entire component
   * @default 0
   */
  delay?: number;
  /**
   * Time in milliseconds to stagger the animation for each child
   * @default 30
   */
  stagger?: number;
  /**
   * Time in milliseconds for the entire animation for each child
   * @default 200
   */
  duration?: number;

  reverseOnExit?: boolean;
  enter: AnimationParams;
  exit: AnimationParams;
}> = (props) => {
  const duration = () => props.duration ?? 200;
  const staggerTime = () => props.stagger ?? 30;
  const delay = () => props.delay ?? 0;

  const totalDuration = () => duration() * refs().length + delay();

  const [refs, setRefs] = createSignal<HTMLElement[]>([]);

  const registerRef = (el: HTMLElement) => {
    setRefs((prev) => [...prev, el]);
  };

  const { isVisible, isMounted } = createPresence(() => props.show, {
    transitionDuration: totalDuration,
  });

  createEffect(() => {
    if (!isMounted()) {
      setRefs([]);
      return;
    }
  });

  createEffect(
    on(isVisible, (visible) => {
      const els = refs();
      if (els.length === 0) return;

      const animation = animate(els, {
        delay: stagger(staggerTime(), { start: delay(), reversed: props.reverseOnExit && !visible }),
        duration: duration(),
        ...(visible ? props.enter : props.exit),
      });
      onCleanup(() => animation.cancel());
    }),
  );

  return (
    <Show when={isMounted()}>
      {props.children({ ref: registerRef, isMounted, isVisible, totalDuration: totalDuration })}
    </Show>
  );
};
